import { fill, t } from '../strings';
import { isCalendarDay } from '../zonedTime';

/**
 * Deal setup by type (piece 18, *New Deal by type* and *The Deal page by type*;
 * decisions 2290, 2293, 2390): what the Parts form checks and writes out, and
 * which of the three pages a Deal draws. 🔒 The page reads the Deal's type and
 * route, never a stored form name.
 */

/** One part on the Parts form: a catalog Item and a quantity, the price where the person changed it. */
export interface PartsDealLine {
  itemId: string | null | undefined;
  quantity: number | null | undefined;
}

/** What the Parts form sends beside its lines, as far as a check before the save reads it. */
export interface PartsDealDraft {
  companyId: string | null | undefined;
  requestedByEmploymentId: string | null | undefined;
  /** A calendar day, written year-month-day, or nothing. */
  expectedDate?: string | null | undefined;
  lines: readonly PartsDealLine[];
}

/**
 * Why the Parts form cannot be saved, or null where it can. 🔒 Authority is
 * required on every deal (piece 18), so who asked is; every line names a catalog
 * Item, because the Quote's line does and the Quote is the list (decision 2511);
 * the Expected date is optional.
 */
export function partsDealProblem(draft: PartsDealDraft): string | null {
  if (!draft.companyId) return 'a parts order names its account';
  if (!draft.requestedByEmploymentId) return 'a parts order names who asked for the parts';
  if (draft.lines.length === 0) return t('deal.parts.noLines');
  for (const [index, line] of draft.lines.entries()) {
    if (!line.itemId) return fill(t('deal.parts.freeTextLine'), { part: String(index + 1) });
    if (!(typeof line.quantity === 'number' && line.quantity > 0)) return `part ${index + 1} carries a quantity above zero`;
  }
  const day = draft.expectedDate ?? null;
  if (day !== null && day !== '' && !isCalendarDay(day)) return 'the Expected date is a calendar day, written year-month-day';
  return null;
}

/** 🔒 The most of the first part's name a Parts deal's default name carries (decision 2620). */
export const PARTS_NAME_PART_LIMIT = 40;

/**
 * 🔒 The first part's name as a Parts deal's default name carries it (decision
 * 2620; T1877): whole where it runs to the limit or less, else cut at the last
 * word boundary inside the limit and ended with an ellipsis. A catalog item
 * imported from Zoho carries its whole description as its name, and the title
 * read the description. A space, a comma, a semicolon or a colon ends a word; a
 * name with no boundary inside the limit is cut at the limit itself.
 */
export function partsDealPartName(name: string, limit: number = PARTS_NAME_PART_LIMIT): string {
  // 🔨 Cut by code point, not by UTF-16 unit, so a surrogate pair straddling the limit never leaves a lone half before the ellipsis (T1822).
  const chars = [...name.trim().replace(/\s+/g, ' ')];
  if (chars.length <= limit) return chars.join('');
  let end = limit;
  while (end > 0 && !/[\s,;:]/.test(chars[end] ?? '')) end -= 1;
  const cut = (at: number): string => chars.slice(0, at).join('');
  const head = (end > 0 ? cut(end) : cut(limit)).replace(/[\s,;:.\-/]+$/, '');
  return `${head || cut(limit)}…`;
}

/**
 * The name a Deal from the Parts form carries, which asks no name: the account
 * and its first part, cut to its first forty characters or so (decision 2620),
 * and how many more follow. The title's Edit renames it (decision 1932).
 */
export function partsDealName(account: string, itemNames: readonly string[]): string {
  const first = partsDealPartName(itemNames[0] ?? '');
  const more = itemNames.length - 1;
  const template = more > 0 ? t('deal.parts.defaultNameMore') : t('deal.parts.defaultName');
  return fill(template, { account, item: first, count: String(more) });
}

/** 🔒 The Need a Parts deal answers with no *In their words* line: the parts written out, one quantity and name each (decision 2290). */
export function partsNeedText(lines: readonly { quantity: number; itemName: string }[]): string {
  return lines.map((line) => `${line.quantity} × ${line.itemName}`).join('; ');
}

/** The three Deal pages (decision 2293). */
export type DealPageKind = 'full' | 'parts' | 'request';

/**
 * 🔒 Which page a Deal draws (decision 2293): the request page on the request
 * and the emergency routes, the Parts page for a Parts deal on the Quick route,
 * and the full page for every Project and every other Deal.
 */
export function dealPageKind(deal: { dealType: string; origin: string }): DealPageKind {
  if (deal.origin === 'ServiceRequest' || deal.origin === 'Emergency') return 'request';
  if (deal.dealType === 'Parts' && deal.origin === 'QuickBooked') return 'parts';
  return 'full';
}

/**
 * 🔒 The rows each page's cards carry, stage by stage, as piece 18's table of
 * bands names them. The full page carries every row, so it names none. A stage
 * a page leaves out is absent, and its rows keep their data (decision 2293).
 */
export const DEAL_PAGE_ROWS: Readonly<Record<Exclude<DealPageKind, 'full'>, Readonly<Record<string, readonly string[]>>>> = {
  parts: { Evaluation: ['BOMBuilt', 'RFQIssued'], Won: ['IssueSalesOrder', 'Billed', 'PaymentReceived'] },
  request: { Won: ['CreateWorkOrder', 'WorkInProgress', 'Billed', 'PaymentReceived'] },
};

/**
 * Whether a page draws a stage's card and its ladder rung. 🔒 The Parts page
 * draws its Won card only once the accepted Quote wins the Deal (decision 2390);
 * until then the Quote's state stands in its place.
 */
export function dealPageDrawsStage(kind: DealPageKind, stage: string, dealStage: string): boolean {
  if (kind === 'full') return true;
  if (kind === 'parts' && stage === 'Won' && dealStage !== 'Won') return false;
  return stage in DEAL_PAGE_ROWS[kind];
}

/**
 * 🔒 Whether a page's ladder lists a row its card carries (decision 2511): the
 * Parts page drops BOM Built where the Deal carries no bill of materials, because
 * its Quote is the list. The card still draws the row, not applicable, with the
 * Bill of materials tile's + New beneath it.
 */
export function dealPageLaddersRow(kind: DealPageKind, stage: string, subStage: string, carriesList: boolean): boolean {
  if (kind === 'parts' && subStage === 'BOMBuilt' && !carriesList) return false;
  return dealPageDrawsRow(kind, stage, subStage);
}

/** Whether a page's card for a stage carries a sub-stage row; the full page carries every one. */
export function dealPageDrawsRow(kind: DealPageKind, stage: string, subStage: string): boolean {
  return kind === 'full' || (DEAL_PAGE_ROWS[kind][stage]?.includes(subStage) ?? false);
}

/**
 * 🔒 The stages a Parts page's Deal never stands at (decision 2430; T1722): its
 * accepted Quote takes it from Evaluation to Won (decision 2390), and the page
 * draws no Discovery, Negotiation or Decision card to work it there (Discovery
 * since decision 5450).
 */
export const PARTS_PAGE_SKIPPED_STAGES: readonly string[] = ['Discovery', 'Negotiation', 'Decision'];

/** Whether a move to `stage` is refused for a Deal drawing `kind` (decision 2430): a Parts page's Deal is never moved to Negotiation or Decision. */
export function partsStageRefused(kind: DealPageKind, stage: string): boolean {
  return kind === 'parts' && PARTS_PAGE_SKIPPED_STAGES.includes(stage);
}

/** The Quote's three states the Parts page names while its Deal is open (decision 2390). */
export const PARTS_QUOTE_STEPS = ['Draft', 'Sent', 'Accepted'] as const;
export type PartsQuoteStep = (typeof PARTS_QUOTE_STEPS)[number];

/**
 * 🔒 Where a Parts deal's Quote stands, as the Parts page draws it (decision
 * 2390): a Draft, a Quote that reached the customer, or an accepted one. A
 * Quote declined, voided or expired reads none of the three, and its pill says why.
 */
export function partsQuoteStep(status: string): PartsQuoteStep | null {
  if (status === 'Draft') return 'Draft';
  if (status === 'Sent' || status === 'ClientViewed' || status === 'Sending' || status === 'DeliveryFailed') return 'Sent';
  if (status === 'Accepted') return 'Accepted';
  return null;
}
